import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ModelOption } from '@/components/landing/quote-form'

import { Warehouse } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/landing/page-header'
import { QuoteForm } from '@/components/landing/quote-form'
import { getModels } from '@/lib/content'
import { QUOTE_STEPS } from '@/constants/content'

interface ContactPageProps {
    models: ModelOption[]
}

const ContactPage: FC<ContactPageProps> = ({ models }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Request a quote"
                description="Tell us about the tiny house you have in mind. We will reply personally with ideas, a realistic price range and the next steps, with no obligation."
            />

            <PageHeader
                eyebrow="Request a quote"
                title="Let's talk about your tiny house"
                description="Share a few details about your plans and we will come back to you personally with ideas, a realistic price range and the next steps. No obligation, no pressure."
            />

            <section id='quote' className='py-14 lg:py-20'>
                <div className='mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-14'>
                    <div className='lg:col-span-8'>
                        <QuoteForm models={models} />
                    </div>

                    <aside className='space-y-6 lg:col-span-4'>
                        <div className='rounded-[2rem] bg-forest p-7 text-forest-foreground sm:p-8'>
                            <h2 className='font-heading text-2xl font-medium'>
                                What happens next
                            </h2>

                            <ol className='mt-7 space-y-6'>
                                {QUOTE_STEPS.map((step, index) => (
                                    <li key={step.title} className='flex gap-4'>
                                        <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-forest-foreground/10 font-heading text-lg'>
                                            {index + 1}
                                        </span>

                                        <div>
                                            <h3 className='font-semibold'>
                                                {step.title}
                                            </h3>

                                            <p className='mt-1 text-sm leading-relaxed text-forest-foreground/70'>
                                                {step.text}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div id='visit' className='rounded-[2rem] border border-border bg-card p-7 sm:p-8'>
                            <span className='flex size-12 items-center justify-center rounded-xl bg-secondary text-primary'>
                                <Warehouse className='size-6' aria-hidden='true' />
                            </span>

                            <h2 className='mt-5 font-heading text-2xl font-medium'>
                                Visit the workshop
                            </h2>

                            <p className='mt-3 leading-relaxed text-muted-foreground'>
                                See homes in progress and meet the team. Mention it in your message and we will suggest a few dates that work for you.
                            </p>
                        </div>
                    </aside>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactPageProps> = async () => {
    try {
        const models = await getModels()

        return {
            props: {
                models: models.map(({ id, slug, name, price, length }) => ({ id, slug, name, price, length }))
            },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Models for the quote form could not be loaded:', error)

        return { props: { models: [] }, revalidate: 10 }
    }
}

export default ContactPage
